import { BrowserRouter, Route } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';

import { AuthProvider } from '@/components/auth-provider';
import { PrivateRoute } from '@/components/private-route';
import { PublicRoute } from '@/components/public-route';
import { RequireFeature } from '@/components/require-feature';
import { RequireRole } from '@/components/require-role';
import { ScrollToTop } from '@/components/scroll-to-top';
import { TailwindIndicator } from '@/components/tailwind-indicator';
import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from '@/components/ui/sonner';
import { AppLayout } from '@/components/layout/app-layout';
import { EmployeeLayout } from '@/components/layout/employee-layout';
import { LanguageProvider } from '@/contexts/language-context';
import { AcceptInvite } from '@/routes/accept-invite';
import { Login } from '@/routes/login';
import { Onboarding } from '@/routes/onboarding';
import { Jobs } from '@/routes/jobs';
import { JobDetail } from '@/routes/job-detail';
import { Quotes } from '@/routes/quotes';
import { Invoices } from '@/routes/invoices';
import { Customers } from '@/routes/customers';
import { BusinessPage } from '@/routes/business-page';
import { Billing } from '@/routes/billing';
import { BillingSuccess } from '@/routes/billing-success';
import { BillingCancel } from '@/routes/billing-cancel';
import { Employees } from '@/routes/employees';
import { EmployeeSchedule } from '@/routes/employee-schedule';
import { EmployeeVisitDetail } from '@/routes/employee-visit-detail';
import { Payments } from '@/routes/payments';
import { Payroll } from '@/routes/payroll';
import { PayrollEmployeeDetail } from '@/routes/payroll/employee-detail';
import { NotFound } from '@/routes/not-found';
import { PublicQuote } from '@/routes/public-quote';
import { PublicInvoice } from '@/routes/public-invoice';
import { SentryRoutes } from '@/lib/sentry';
import { queryClient } from '@/lib/query-client';

export function AppRoutes() {
  return (
    <BrowserRouter>
      <NuqsAdapter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <LanguageProvider>
              <ThemeProvider>
                <SentryRoutes>
                  {/* Public (unauthenticated) routes */}
                  <Route element={<PublicRoute />}>
                    <Route path="/" element={<Login />} />
                  </Route>

                  {/* Share link — unauthenticated, no app shell, no redirect
                      (bare route: PublicRoute would bounce a signed-in owner
                      previewing their own link). */}
                  <Route path="/q/:token" element={<PublicQuote />} />
                  <Route path="/i/:token" element={<PublicInvoice />} />

                  {/* Employee invite — unauthenticated, no app shell. Bare route:
                      PrivateRoute would bounce the not-yet-signed-in employee
                      before signInWithEmailLink can run; PublicRoute would
                      bounce them right after it succeeds. */}
                  <Route path="/accept-invite" element={<AcceptInvite />} />

                  {/* Private routes — onboarding (no app shell) */}
                  <Route element={<PrivateRoute />}>
                    <Route path="/onboarding" element={<Onboarding />} />
                  </Route>

                  {/* Private routes — inside app shell, split by role */}
                  <Route element={<PrivateRoute />}>
                    <Route element={<RequireRole role="employee" />}>
                      <Route element={<EmployeeLayout />}>
                        <Route path="/schedule" element={<EmployeeSchedule />} />
                        <Route
                          path="/schedule/:jobId/:visitId"
                          element={<EmployeeVisitDetail />}
                        />
                      </Route>
                    </Route>

                    <Route element={<RequireRole role="owner" />}>
                      <Route element={<AppLayout />}>
                        <Route path="/jobs" element={<Jobs />} />
                        <Route path="/jobs/:jobId" element={<JobDetail />} />
                        <Route path="/quotes" element={<Quotes />} />
                        <Route path="/invoices" element={<Invoices />} />
                        <Route path="/customers" element={<Customers />} />
                        <Route path="/business-page" element={<BusinessPage />} />
                        <Route path="/billing" element={<Billing />} />
                        <Route path="/billing/success" element={<BillingSuccess />} />
                        <Route path="/billing/cancel" element={<BillingCancel />} />
                        <Route element={<RequireFeature feature="employees" />}>
                          <Route path="/employees" element={<Employees />} />
                        </Route>
                        <Route element={<RequireFeature feature="payroll" />}>
                          <Route path="/payroll" element={<Payroll />} />
                          <Route
                            path="/payroll/:employeeId"
                            element={<PayrollEmployeeDetail />}
                          />
                        </Route>
                        <Route path="/payments" element={<Payments />} />
                      </Route>
                    </Route>
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </SentryRoutes>
                <ScrollToTop />
                <TailwindIndicator />
                <Toaster position="bottom-center" richColors />
              </ThemeProvider>
            </LanguageProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </AuthProvider>
        </QueryClientProvider>
      </NuqsAdapter>
    </BrowserRouter>
  );
}
